import NiceModal, { type NiceModalHandler } from "@ebay/nice-modal-react";
import {
    addLoadedStepper,
    StepperType,
    useAllowedChanges,
    useInvestmentStrategyMutation,
    useInvestorProfileQuestionnaireQuestions,
    useInvestorQuestionnaireConfig,
    useQuestionnairePdfMutation,
    useResolveInvestorProfileQuestionnaireMutation,
    useSetDPSAmlIdentificationMutation,
    useSetPPAmlIdentificationMutation,
    useSmsVerificationCodeMutation,
} from "@repo/logic";
import {
    type BankIdStepExtraProps,
    ChangePensionContributionStepper,
    ModalConfirm,
    type QuestionnaireQueryType,
    ChangePensionContributionStyled as Styled,
    useStableLatestObject,
} from "@repo/ui";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { usePhoneNumbers, usePrecontractualDocs } from "../../queryHooks/misc";
import { usePensionDetail } from "../../queryHooks/shared";
import { useRedirectToContributionWithQuestionare } from "./useRedirectToContributionWithQuestionare";

const PENSION_QUESTIONNAIRE_STEPPER_MODAL_WIDTH = 1000;

export const ChangePensionContributionStepperWrapper: FC = () => {
    const dispatch = useDispatch();
    const resolveInvestorProfileMutation = useResolveInvestorProfileQuestionnaireMutation();
    const sendSMSVerificationCodeMutation = useSmsVerificationCodeMutation();
    const sendInvestmentStrategyRequestMutation = useInvestmentStrategyMutation();
    const questionsQuery = useInvestorProfileQuestionnaireQuestions({ options: { enabled: false } });
    const questionnaireConfigQuery = useInvestorQuestionnaireConfig({ options: { enabled: false } });
    const precontractualDocsQuery = usePrecontractualDocs({ options: { enabled: false } });
    const phoneNumbersQuery = usePhoneNumbers({ options: { enabled: false } });
    const setDPSAmlIdentificationMutation = useSetDPSAmlIdentificationMutation();
    const setPPAmlIdentificationMutation = useSetPPAmlIdentificationMutation();
    const questionnairePdfMutation = useQuestionnairePdfMutation();

    useEffect(() => {
        dispatch(addLoadedStepper({ stepper: StepperType.ChangePensionContribution }));
    }, [dispatch]);

    // Stable reference object containing evolving query / mutation instances.
    const queries = useStableLatestObject<QuestionnaireQueryType["queries"]>({
        questionsQuery,
        resolveInvestorProfileMutation,
        questionnaireConfigQuery,
        precontractualDocsQuery,
        phoneNumbersQuery,
        sendSMSVerificationCodeMutation,
        usePensionDetail,
        sendInvestmentStrategyRequestMutation,
        setDPSAmlIdentificationMutation,
        setPPAmlIdentificationMutation,
        questionnairePdfMutation,
        useAllowedChanges,
    });

    // vyvolání modalu při redirectu z bankid aplikace
    useRedirectToContributionWithQuestionare();

    // Memoize modal components mapping to keep stable identity
    const modalComponents = useMemo(
        () => ({
            PaperComponent: Styled.StepperCustomPaper,
            ModalTitleComponent: Styled.StepperInvisibleBox,
            DialogActionsWrapperComponent: Styled.StepperInvisibleBox,
            DialogContentWrapperComponent: Styled.StepperCustomStack,
            DialogComponent: Styled.StepperCustomDialog,
        }),
        [],
    );

    // Memoize close override handler
    const handleCloseOverride = useCallback(
        ({ preventDefault }: { preventDefault: () => void; reason: unknown; modal: unknown }) => {
            // close action is not handled here - it is managed by ChangeSavingsStrategyStepper
            preventDefault();
        },
        [],
    );

    // Memoized factory producing closeAction (captures parentModal per invocation)
    const createCloseAction = useCallback(
        (parentModal?: NiceModalHandler | null) => () => {
            parentModal?.resolve();
            parentModal?.hide();
            // Fallback: use imperative hide with modal ID in case parentModal ref is stale
            NiceModal.hide("Nicemodal-ChangePensionContributionStepperModal");
        },
        [],
    );

    // Memoize content renderer passed to ModalConfirm (analogy with PensionQuestionnaireStepperWrapper)
    const renderContent = useCallback(
        ({
            parentModal,
            bankIdcompleted,
            bankIdErrorMessage,
            bankIdAuthorizationUri,
            beforeBankIdAuthorizationAction,
            contractId,
            contractType,
            scrollToEmployerSection,
        }: BankIdStepExtraProps & { parentModal?: NiceModalHandler | null }) => {
            return (
                <ChangePensionContributionStepper
                    bankIdErrorMessage={bankIdErrorMessage}
                    bankIdcompleted={bankIdcompleted}
                    contractId={contractId}
                    contractType={contractType}
                    closeAction={createCloseAction(parentModal)}
                    queries={queries}
                    bankIdAuthorizationUri={bankIdAuthorizationUri}
                    beforeBankIdAuthorizationAction={beforeBankIdAuthorizationAction}
                    scrollToEmployerSection={scrollToEmployerSection}
                />
            );
        },
        [queries, createCloseAction],
    );

    return (
        <ModalConfirm<BankIdStepExtraProps>
            title=""
            id="Nicemodal-ChangePensionContributionStepperModal"
            content={renderContent}
            width={PENSION_QUESTIONNAIRE_STEPPER_MODAL_WIDTH}
            components={modalComponents}
            onCloseOverride={handleCloseOverride}
        />
    );
};
